import { useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';

export interface AIMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tokens?: number;
}

export type AIStatus = 'idle' | 'loading' | 'error';
export type AIBlockReason = 'plan_blocked' | 'limit_reached' | 'ai_disabled' | null;

export const useAIAssistant = () => {
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [status, setStatus] = useState<AIStatus>('idle');
  const [blockReason, setBlockReason] = useState<AIBlockReason>(null);
  const [blockMessage, setBlockMessage] = useState('');
  const sessionId = useRef(`session_${Date.now()}`);

  const addMessage = (role: AIMessage['role'], content: string, tokens?: number) => {
    const msg: AIMessage = {
      id: `${Date.now()}_${Math.random()}`,
      role,
      content,
      timestamp: new Date(),
      tokens,
    };
    setMessages(prev => [...prev, msg]);
    return msg;
  };

  const sendMessage = useCallback(async (question: string): Promise<void> => {
    if (!question.trim() || status === 'loading') return;

    addMessage('user', question);
    setStatus('loading');
    setBlockReason(null);

    trackEvent('ai_question_sent', { question_length: question.length });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        addMessage('assistant', 'Sua sessão expirou. Faça login novamente para continuar.');
        setStatus('idle');
        return;
      }

      let response: Response;
      try {
        response = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/process-user-question`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ question, session_id: sessionId.current }),
          }
        );
      } catch (_fetchErr) {
        addMessage('assistant', 'Não foi possível conectar ao assistente. Verifique sua conexão com a internet e tente novamente.');
        trackEvent('ai_error', { error: 'network_failure' });
        setStatus('error');
        setTimeout(() => setStatus('idle'), 2000);
        return;
      }

      let data: {
        answer?: string;
        success?: boolean;
        tokens_used?: number;
        error?: string;
        message?: string;
      };

      try {
        data = await response.json();
      } catch (_parseErr) {
        addMessage('assistant', 'Recebi uma resposta inesperada do servidor. Tente novamente.');
        setStatus('error');
        setTimeout(() => setStatus('idle'), 2000);
        return;
      }

      if (!response.ok) {
        const errorCode = data.error as string;

        // Handle plan/access blocks (show upgrade prompt)
        if (errorCode === 'plan_blocked' || errorCode === 'limit_reached' || errorCode === 'ai_disabled') {
          setBlockReason(errorCode as AIBlockReason);
          setBlockMessage(data.message || 'Acesso não disponível.');
          trackEvent('ai_blocked_by_plan', { reason: errorCode });
          setStatus('idle');
          return;
        }

        // Session / auth errors
        if (errorCode === 'session_expired' || errorCode === 'missing_token' || errorCode === 'invalid_token' || response.status === 401) {
          addMessage('assistant', 'Sua sessão expirou. Recarregue a página e faça login novamente.');
          setStatus('idle');
          return;
        }

        // AI provider not configured
        if (errorCode === 'no_api_key' || errorCode === 'ai_disabled') {
          addMessage('assistant', data.message || 'O assistente não está disponível no momento. Entre em contato com o suporte.');
          setStatus('idle');
          return;
        }

        // Timeout
        if (errorCode === 'timeout' || response.status === 408) {
          addMessage('assistant', 'A resposta demorou mais do que o esperado. Tente uma pergunta mais simples ou tente novamente.');
          setStatus('idle');
          return;
        }

        // Server error
        if (response.status >= 500) {
          addMessage('assistant', 'O servidor encontrou um problema temporário. Aguarde um momento e tente novamente.');
          setStatus('error');
          setTimeout(() => setStatus('idle'), 2000);
          return;
        }

        // Generic HTTP error — use server message if available
        addMessage('assistant', data.message || 'Não consegui processar sua pergunta. Tente novamente.');
        trackEvent('ai_error', { error: errorCode, status: response.status });
        setStatus('idle');
        return;
      }

      // Success
      addMessage('assistant', data.answer || 'Não obtive uma resposta. Tente reformular sua pergunta.', data.tokens_used);
      trackEvent('ai_response_received', { tokens: data.tokens_used, success: data.success });
      setStatus('idle');

    } catch (err) {
      const msg = (err as Error).message || 'unknown';
      trackEvent('ai_error', { error: msg });
      addMessage('assistant', 'Ocorreu um erro inesperado. Verifique sua conexão e tente novamente.');
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2000);
    }
  }, [status]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setBlockReason(null);
    setBlockMessage('');
    sessionId.current = `session_${Date.now()}`;
  }, []);

  return { messages, status, blockReason, blockMessage, sendMessage, clearMessages };
};
